import { authorizationHeader, refreshAccessToken } from '../../lib/auth';
import { API_BASE_URL } from '../../lib/env';
import type { IntakeState } from './types';

/**
 * A hand-rolled SSE reader over `fetch`, rather than the browser's
 * `EventSource`, because `EventSource` cannot attach an Authorization header.
 *
 * Resolves once the stream ends (server closed it) or rejects on any network
 * or HTTP error; the caller (useIntakeState) is what turns either outcome into
 * a reconnect-with-backoff.
 */
export async function readIntakeStream(
  workspaceId: string,
  requestId: string,
  onState: (state: IntakeState) => void,
  signal: AbortSignal,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/workspaces/${encodeURIComponent(workspaceId)}/requests/${encodeURIComponent(requestId)}/intake/stream`,
    {
      method: 'GET',
      headers: { accept: 'text/event-stream', ...(await authorizationHeader()) },
      credentials: 'omit',
      signal,
    },
  );

  // A stream cannot be replayed through the SDK adapter, so refresh here and
  // let the caller's reconnect pick up the new token.
  if (response.status === 401) await refreshAccessToken();

  if (!response.ok || response.body === null) {
    throw new Error(`Intake stream failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    // eslint-disable-next-line no-await-in-loop -- reading a stream: the next chunk does not exist yet
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line; a frame may carry several
    // "data:" lines that join with a newline between them.
    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);

      const dataLines = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart());

      if (dataLines.length > 0) {
        try {
          onState(JSON.parse(dataLines.join('\n')) as IntakeState);
        } catch {
          // One malformed frame should not tear down a stream that is
          // otherwise working — skip it and wait for the next one.
        }
      }

      boundary = buffer.indexOf('\n\n');
    }
  }
}
